<template>
  <div class="cases-page">
    <b-overlay
      :show="loading"
      variant="transparent"
      opacity="0.85"
      blur="2px"
      rounded="sm"
      spinner-variant="primary"
    >
      <!-- Summary Cards -->
      <b-row class="summary-cards-row">
        <!-- Total Debt Card -->
        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="summary-card">
            <div class="d-flex align-items-center">
              <div class="icon-wrapper bg-light-danger">
                <span class="currency-icon text-danger">kr</span>
              </div>
              <div class="ml-1">
                <h6 class="mb-0 text-muted">
                  {{ $t('DEBTIA_TOTAL_DEBT') }}
                </h6>
                <h3 class="mb-0 font-weight-bold">
                  {{ formatCurrency(totalDebt) }}
                </h3>
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Next Payment Card -->
        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="summary-card">
            <div class="d-flex align-items-center">
              <div class="icon-wrapper bg-light-warning">
                <feather-icon
                  icon="CalendarIcon"
                  size="24"
                  class="text-warning"
                />
              </div>
              <div class="ml-1">
                <h6 class="mb-0 text-muted">
                  {{ $t('DEBTIA_NEXT_PAYMENT') }}
                </h6>
                <h3 class="mb-0 font-weight-bold">
                  {{ nextPaymentDate || '—' }}
                </h3>
              </div>
            </div>
          </b-card>
        </b-col>

        <!-- Active Cases Card -->
        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="summary-card">
            <div class="d-flex align-items-center">
              <div class="icon-wrapper bg-light-primary">
                <feather-icon
                  icon="FolderIcon"
                  size="24"
                  class="text-primary"
                />
              </div>
              <div class="ml-1">
                <h6 class="mb-0 text-muted">
                  {{ $t('DEBTIA_ACTIVE_CASES') }}
                </h6>
                <h3 class="mb-0 font-weight-bold">
                  {{ activeCasesCount }}
                </h3>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Cases Header with Filter -->
      <div class="cases-header">
        <h5 class="cases-title">
          <feather-icon
            icon="FolderIcon"
            size="20"
            class="mr-50"
          />
          {{ showClosedCases ? $t('DEBTIA_ALL_CASES') : $t('DEBTIA_ACTIVE_CASES') }}
          <span class="cases-count">({{ filteredCases.length }})</span>
        </h5>
        <b-form-checkbox
          v-model="showClosedCases"
          switch
          class="filter-toggle"
        >
          {{ $t('DEBTIA_SHOW_CLOSED') }}
        </b-form-checkbox>
      </div>

      <!-- Search and Filter Bar -->
      <div class="cases-filters">
        <b-input-group class="search-input">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            :placeholder="$t('DEBTIA_SEARCH_PLACEHOLDER')"
            @input="currentPage = 1"
          />
        </b-input-group>
        <b-form-select
          v-model="selectedStatus"
          :options="statusOptions"
          class="status-filter"
          @change="currentPage = 1"
        />
        <b-form-select
          v-if="uniqueCreditors.length > 1"
          v-model="selectedCreditor"
          class="creditor-filter"
          @change="currentPage = 1"
        >
          <option :value="null">
            {{ $t('DEBTIA_ALL_CREDITORS') }}
          </option>
          <option
            v-for="creditor in uniqueCreditors"
            :key="creditor"
            :value="creditor"
          >
            {{ creditor }}
          </option>
        </b-form-select>
        <span
          v-if="loadingMore"
          class="loading-more-indicator"
        >
          <b-spinner
            small
            variant="primary"
          />
          {{ $t('DEBTIA_LOADING_MORE_CASES') }}
        </span>
      </div>

      <!-- No cases message -->
      <div
        v-if="!loading && filteredCases.length === 0"
        class="no-cases-message"
      >
        <feather-icon
          icon="FolderIcon"
          size="48"
          class="text-muted mb-1"
        />
        <h4 class="text-muted">
          {{ $t('DEBTIA_NO_CASES_FOUND') }}
        </h4>
        <p class="text-muted">
          {{ $t('DEBTIA_NO_ACTIVE_CASES_TEXT') }}
        </p>
      </div>

      <!-- Cases grid -->
      <b-row
        v-else
        class="cases-grid"
      >
        <b-col
          v-for="caseItem in paginatedCases"
          :key="caseItem.id"
          sm="12"
          md="6"
          lg="6"
          xl="4"
        >
          <case-card
            :case-data="caseItem"
            :loading="loadingCaseId === caseItem.id"
            :loading-type="loadingPaymentType"
            @view-plan="handleViewPlan"
            @full-payment="handleFullPayment"
            @create-plan="handleCreatePlan"
          />
        </b-col>
      </b-row>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="cases-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredCases.length"
          :per-page="perPage"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
        <span class="pagination-info">
          {{ $t('DEBTIA_SHOWING_PAGINATION') }} {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, filteredCases.length) }} {{ $t('DEBTIA_OF') }} {{ filteredCases.length }} {{ $t('DEBTIA_CASES_LABEL') }}
        </span>
      </div>
    </b-overlay>

    <!-- Payment Plan Modal -->
    <payment-plan-modal
      :visible="showPaymentPlanModal"
      :case-data="selectedCase"
      :installments="selectedCaseInstallments"
      :plan-data="selectedCasePlanData"
      @close="showPaymentPlanModal = false"
      @pay-next="handlePayNextInstallment"
    />
  </div>
</template>

<script>
import {
  BOverlay, BRow, BCol, BCard, BFormCheckbox, BFormInput, BFormSelect, BPagination, BInputGroup, BInputGroupPrepend, BSpinner,
} from 'bootstrap-vue'
import axios from '@axios'
import axiosRaw from 'axios'
import constants from '@/app-configs/constants'
import CaseCard from './CaseCard.vue'
import PaymentPlanModal from './PaymentPlanModal.vue'

export default {
  name: 'Cases',
  components: {
    BOverlay,
    BRow,
    BCol,
    BCard,
    BFormCheckbox,
    BFormInput,
    BFormSelect,
    BPagination,
    BInputGroup,
    BInputGroupPrepend,
    BSpinner,
    CaseCard,
    PaymentPlanModal,
  },
  data() {
    return {
      loading: true,
      loadingMore: false,
      loadingCaseId: null,
      loadingPaymentType: null,
      cases: [],
      showClosedCases: false,
      showPaymentPlanModal: false,
      selectedCase: {},
      selectedCaseInstallments: [],
      selectedCasePlanData: null,
      installmentCache: {},
      paymentSettings: {},
      adminSettings: [],
      // Pagination
      currentPage: 1,
      perPage: 12,
      // Search and filter
      searchQuery: '',
      selectedCreditor: null,
      selectedStatus: null,
    }
  },
  computed: {
    // Status options for filter dropdown
    statusOptions() {
      return [
        { value: null, text: this.$t('DEBTIA_ALL_STATUSES') },
        { value: 'active', text: this.$t('DEBTIA_STATUS_ACTIVE') },
        { value: 'payment_plan', text: this.$t('DEBTIA_STATUS_PAYMENT_PLAN') },
        { value: 'hold', text: this.$t('DEBTIA_STATUS_HOLD') },
        { value: 'closed', text: this.$t('DEBTIA_STATUS_CLOSED') },
      ]
    },
    // Get unique creditor names for filter dropdown
    uniqueCreditors() {
      const creditors = [...new Set(this.cases.map(c => c.creditorName))]
      return creditors.sort()
    },
    // Filter cases by status, search, and creditor
    filteredCases() {
      let result = this.cases

      // Filter by closed status toggle (legacy, still used)
      if (!this.showClosedCases && !this.selectedStatus) {
        result = result.filter(c => c.status !== 'closed')
      }

      // Filter by selected status
      if (this.selectedStatus) {
        result = result.filter(c => c.status === this.selectedStatus)
      }

      // Filter by search query (case number or creditor name)
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        result = result.filter(c => String(c.journalNumber).toLowerCase().includes(query)
            || c.creditorName.toLowerCase().includes(query))
      }

      // Filter by selected creditor
      if (this.selectedCreditor) {
        result = result.filter(c => c.creditorName === this.selectedCreditor)
      }

      return result
    },
    // Paginated cases for display
    paginatedCases() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredCases.slice(start, end)
    },
    // Total pages for pagination
    totalPages() {
      return Math.ceil(this.filteredCases.length / this.perPage)
    },
    activeCasesCount() {
      return this.cases.filter(c => c.status !== 'closed').length
    },
    totalDebt() {
      // Sum remaining amount from all non-closed cases
      return this.cases
        .filter(c => c.status !== 'closed')
        .reduce((sum, c) => sum + (c.remainingAmount || 0), 0)
    },
    nextPaymentDate() {
      const casesWithPlans = this.cases.filter(c => c.hasPaymentPlan && c.status !== 'closed' && c.nextPaymentDate)
      if (casesWithPlans.length === 0) return null
      // Find earliest next payment date
      const sorted = [...casesWithPlans].sort((a, b) => {
        const dateA = this.parseDisplayDate(a.nextPaymentDate)
        const dateB = this.parseDisplayDate(b.nextPaymentDate)
        return dateA - dateB
      })
      return sorted[0].nextPaymentDate
    },
  },
  created() {
    this.fetchCases()
    this.fetchAdminSettings()
  },
  methods: {
    async fetchCases() {
      this.loading = true
      this.cases = []
      let page = 1
      const pageSize = 100

      try {
        // Keep fetching until we get no more cases
        let hasMore = true
        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get(`${constants.baseUrlV2}/debt-cases/list?perPage=${pageSize}&page=${page}`)

          if (response.status === 200) {
            const responseData = response.data.data
            const apiCases = responseData.data || responseData || []

            // If no cases returned, we're done
            if (!apiCases || apiCases.length === 0) {
              hasMore = false
              break
            }

            const validCases = apiCases.filter(c => [1, 3, 4].includes(c.status))
            const mappedCases = validCases.map(caseItem => this.mapCaseData(caseItem))

            // Add cases to the list
            this.cases = [...this.cases, ...mappedCases]

            // Hide main loading after first batch, show "loading more" indicator
            if (page === 1) {
              this.loading = false
              this.loadingMore = true
            }

            // If we got fewer cases than requested, we've reached the end
            if (apiCases.length < pageSize) {
              hasMore = false
            } else {
              page += 1
            }
          } else {
            hasMore = false
          }
        }
      } catch (error) {
        console.error('Error fetching cases:', error)
        if (page === 1) {
          this.cases = []
        }
      } finally {
        this.loading = false
        this.loadingMore = false
        // Fetch installment plan data for cases with payment plans
        this.fetchAllInstallmentPlans()
      }
    },

    mapCaseData(caseItem) {
      const { statements = [] } = caseItem

      // Use case id as journal number
      const journalNumber = caseItem.id

      // Extract amounts from statements by job_task_no
      // 1000 = Hovedstol (principal)
      // 1001 = Renter (interest)
      // 1002-1006 = Omkostninger/Gebyrer (fees)
      const principalStatement = statements.find(s => s.job_task_no === '1000')
      const interestStatement = statements.find(s => s.job_task_no === '1001')
      const feeStatements = statements.filter(s => ['1002', '1003', '1004', '1005', '1006'].includes(s.job_task_no))

      const principal = parseFloat(principalStatement?.schedule_total_price) || 0
      const interest = parseFloat(interestStatement?.schedule_total_price) || 0
      const fees = feeStatements.reduce((sum, s) => sum + (parseFloat(s.schedule_total_price) || 0), 0)

      // Calculate total and paid amounts from all statements (excluding summary/system rows)
      const excludedRows = ['0000', '9999', '99999']
      const relevantStatements = statements.filter(s => !excludedRows.includes(s.job_task_no))
      const totalAmount = relevantStatements.reduce((sum, s) => sum + (parseFloat(s.schedule_total_price) || 0), 0)
      const paidAmount = relevantStatements.reduce((sum, s) => sum + (parseFloat(s.paid_amount) || 0), 0)
      const remainingAmount = totalAmount - paidAmount

      // Determine display status
      // API status codes:
      // 0 = pending approval (filtered out)
      // 1 = active
      // 2 = rejected (filtered out)
      // 3 = closed
      // 4 = hold (from datacenter, but shown as active for debtor)
      //
      // Display status logic:
      // - Status 3 → 'closed'
      // - Status 1 or 4 with installment_plan active → 'payment_plan'
      // - Status 1 or 4 with objection (indsigelse) → 'hold' (TODO: add objection check)
      // - Status 1 or 4 → 'active'
      let status = 'active'
      const hasPaymentPlan = caseItem.installment_plan?.toLowerCase() === 'active'
      // TODO: Add objection check when API provides this data
      // const hasObjection = caseItem.has_objection || false

      if (caseItem.status === 3) {
        status = 'closed'
      } else if (hasPaymentPlan) {
        status = 'payment_plan'
      // } else if (hasObjection) {
      //   status = 'hold'
      } else {
        // Status 1 and 4 are both shown as active
        status = 'active'
      }

      return {
        id: caseItem.id,
        creditorName: caseItem.client_name || this.$t('DEBTIA_UNKNOWN_CREDITOR'),
        journalNumber,
        status,
        principal,
        interest,
        fees,
        totalAmount,
        paidAmount,
        remainingAmount,
        hasPaymentPlan,
        statementsEmpty: statements.length === 0,
        rawData: caseItem,
      }
    },

    formatCurrency(value) {
      if (!value && value !== 0) return '—'
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
      }).format(value)
    },

    async handleViewPlan(caseData) {
      this.selectedCase = caseData

      // Use cached data or fetch from API
      let planData = this.installmentCache[caseData.id]
      if (!planData) {
        planData = await this.fetchInstallments(caseData.id)
        if (planData) {
          this.installmentCache[caseData.id] = planData
        }
      }

      if (planData) {
        this.selectedCaseInstallments = this.mapInstallments(planData)
        this.selectedCasePlanData = planData
      } else {
        this.selectedCaseInstallments = []
        this.selectedCasePlanData = null
      }

      this.showPaymentPlanModal = true
    },

    handlePayNextInstallment(caseData) {
      // TODO: Implement pay next installment flow
      console.log('Pay next installment for:', caseData)
      this.showPaymentPlanModal = false
    },

    async handleFullPayment(caseData) {
      this.loadingCaseId = caseData.id
      this.loadingPaymentType = 'full'
      try {
        await this.submitPayment(caseData, true)
      } finally {
        this.loadingCaseId = null
        this.loadingPaymentType = null
      }
    },

    async handleCreatePlan(caseData) {
      this.loadingCaseId = caseData.id
      this.loadingPaymentType = 'plan'
      try {
        await this.submitPayment(caseData, false)
      } finally {
        this.loadingCaseId = null
        this.loadingPaymentType = null
      }
    },

    async fetchAdminSettings() {
      try {
        const headers = {
          clientId: constants.clientID,
          secret: constants.secret,
        }
        const response = await axios.get(
          `${constants.serverBaseUrl}/api/v1/external/debtiaapp/user-settings/get-all`,
          { headers },
        )
        if (response.status === 200 && response.data && response.data.data) {
          this.adminSettings = response.data.data
        }
      } catch (error) {
        console.error('Error fetching admin settings:', error.response?.status, error.response?.data || error.message)
      }
    },

    getSettingValue(key) {
      const item = this.adminSettings.find(i => i.key === key)
      return item ? item.value : null
    },

    getFirstDateOfNextMonth() {
      const today = new Date()
      const year = today.getFullYear()
      const month = today.getMonth()
      const firstOfNextMonth = new Date(year, month + 1, 1)
      return `${firstOfNextMonth.getFullYear()}-${
        String(firstOfNextMonth.getMonth() + 1).padStart(2, '0')}-${
        String(firstOfNextMonth.getDate()).padStart(2, '0')}`
    },

    async submitPayment(caseData, isFullPayment) {
      // Fetch case details to get bc_case_id
      let bcCaseId = caseData.id

      try {
        const detailResponse = await axios.get(`${constants.baseUrl}/debt-case/${caseData.id}`)
        if (detailResponse.status === 200) {
          const caseDetail = detailResponse.data.data
          bcCaseId = caseDetail.debt_case?.bc_case_id || caseData.id
        }
      } catch (err) {
        console.error('Error fetching case details for payment:', err)
      }

      // Ensure admin settings are loaded
      if (this.adminSettings.length === 0) {
        await this.fetchAdminSettings()
      }

      const userData = JSON.parse(localStorage.getItem('userData'))

      const details = {
        journal_id: bcCaseId,
        debtia_debt_case_id: caseData.id,
        period_based_plan: isFullPayment ? 0 : Number(this.getSettingValue('period_base_plan')),
        amount_based_plan: isFullPayment ? 0 : Number(this.getSettingValue('amount_base_plan')),
        max_months: Number(this.getSettingValue('max_amount')),
        min_amount: Number(this.getSettingValue('min_amount')),
        interest_free_months: Number(this.getSettingValue('interest_free_months')),
        payment_method: 4,
        payment_date: this.getFirstDateOfNextMonth(),
        send_method: 'none',
        employee_name: userData?.data?.name || 'User',
      }

      const headers = {
        clientId: constants.debtiaappClientID,
        secret: constants.debtiaappSecret,
      }

      try {
        const response = await axiosRaw.post(
          `${constants.paymentsBaseUrl}/installment_plan/generate_agreement_link`,
          details,
          { headers },
        )

        const tokenUrl = response.data.tokenized_url
        if (!tokenUrl) {
          console.error('No tokenized_url in response:', response.data)
          alert(this.$t('DEBTIA_NO_PAYMENT_LINK'))
          return
        }
        window.open(tokenUrl, '_blank')

        // Save to local backend
        const saveData = {
          ...details,
          token: tokenUrl.split('/').pop(),
          debt_case_id: caseData.id,
        }
        await axios.post(`${constants.baseUrl}/installment-plan/save`, saveData)
      } catch (error) {
        console.error('Payment error:', error)
        if (error.response?.data?.message) {
          const msg = error.response.data.message
          if (msg.includes('active installment plan already exists')) {
            alert(this.$t('DEBTIA_EXISTING_PAYMENT_PLAN'))
          } else if (msg.includes('payment has been registered')) {
            alert(this.$t('DEBTIA_PAYMENT_REGISTERED'))
          } else {
            alert(this.$t('DEBTIA_GENERAL_ERROR'))
          }
        }
      }
    },

    async fetchInstallments(caseId) {
      try {
        const response = await axiosRaw.get(
          `${constants.paymentsBaseUrl}/installment_plan/get_installments`,
          {
            params: { journal_id: caseId },
            headers: {
              clientId: constants.debtiaappClientID,
              secret: constants.debtiaappSecret,
            },
          },
        )
        if (response.status === 200 && response.data.data) {
          return response.data.data
        }
      } catch (error) {
        console.error(`Error fetching installments for case ${caseId}:`, error)
      }
      return null
    },

    async fetchAllInstallmentPlans() {
      const casesWithPlans = this.cases.filter(c => c.hasPaymentPlan && c.status !== 'closed')
      if (casesWithPlans.length === 0) return

      const promises = casesWithPlans.map(async caseItem => {
        const planData = await this.fetchInstallments(caseItem.id)
        if (planData) {
          this.installmentCache[caseItem.id] = planData

          // Enrich case data with installment info
          const paidCount = (planData.installments_data || []).filter(i => i.is_paid).length
          const index = this.cases.findIndex(c => c.id === caseItem.id)
          if (index !== -1) {
            this.$set(this.cases, index, {
              ...this.cases[index],
              paidInstallments: paidCount,
              totalInstallments: planData.total_installments,
              nextPaymentDate: this.formatApiDate(planData.next_unpaid_installment_date),
              nextPaymentAmount: planData.next_unpaid_installment_amount,
            })
          }
        }
      })

      await Promise.all(promises)
    },

    mapInstallments(planData) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return (planData.installments_data || []).map((inst, index) => {
        let status = 'pending'
        if (inst.is_paid) {
          status = 'paid'
        } else {
          // Parse "DD-MM-YYYY" date to check if overdue
          const parts = inst.installment_date.split('-')
          const dueDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`)
          if (dueDate < today) {
            status = 'overdue'
          }
        }

        return {
          rate_number: index + 1,
          due_date: inst.installment_date,
          amount: inst.installment_amount,
          remaining_amount: inst.remaining_amount,
          interest: parseFloat(inst.interest) || 0,
          status,
          payment_date: inst.payment_date || null,
        }
      })
    },

    formatApiDate(dateStr) {
      if (!dateStr) return null
      // Input: "2025-07-01" (YYYY-MM-DD) → Output: "01-07-2025"
      const [year, month, day] = dateStr.split('-')
      return `${day}-${month}-${year}`
    },

    parseDisplayDate(dateStr) {
      if (!dateStr) return new Date(9999, 0)
      // Input: "01-07-2025" (DD-MM-YYYY)
      const [day, month, year] = dateStr.split('-')
      return new Date(`${year}-${month}-${day}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.cases-page {
  .summary-cards-row {
    margin-bottom: 1.5rem;
  }

  .summary-card {
    .icon-wrapper {
      width: 2.4rem;
      height: 2.4rem;
      border-radius: 0.6rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .currency-icon {
      font-size: 1.125rem;
      font-weight: 700;
      text-transform: lowercase;
    }
  }

  .cases-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    padding: 0;

    .cases-title {
      display: flex;
      align-items: center;
      margin: 0;
      font-weight: 600;
      color: #5e5873;

      .cases-count {
        font-weight: 400;
        color: #b9b9c3;
        margin-left: 0.35rem;
      }
    }

    .filter-toggle {
      font-size: 0.81rem;
    }
  }

  .cases-filters {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;

    .search-input {
      flex: 1;
      min-width: 180px;
      max-width: clamp(220px, 25vw, 350px);

      .input-group-text {
        background: rgba(255, 255, 255, 0.7);
        border-color: rgba(0, 0, 0, 0.1);
      }

      input {
        background: rgba(255, 255, 255, 0.7);
        border-color: rgba(0, 0, 0, 0.1);
        font-size: 0.85rem;

        &::placeholder {
          color: #b9b9c3;
        }
      }
    }

    .status-filter {
      min-width: 130px;
      max-width: clamp(140px, 12vw, 180px);
      background: rgba(255, 255, 255, 0.7);
      border-color: rgba(0, 0, 0, 0.1);
      font-size: 0.85rem;
    }

    .creditor-filter {
      min-width: 150px;
      max-width: clamp(180px, 16vw, 250px);
      background: rgba(255, 255, 255, 0.7);
      border-color: rgba(0, 0, 0, 0.1);
      font-size: 0.85rem;
    }

    .loading-more-indicator {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8rem;
      color: #6e6b7b;
      margin-left: auto;
    }
  }

  .cases-grid {
    row-gap: 2rem;
  }

  .cases-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    .pagination {
      margin: 0;
    }

    .pagination-info {
      font-size: 0.8rem;
      color: #6e6b7b;
    }
  }

  .no-cases-message {
    text-align: center;
    padding: 4rem 2rem;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 1rem;
    backdrop-filter: blur(20px);
  }
}
</style>
